import React, { useCallback, useEffect, useRef, useState } from 'react';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { SharedStyles as Shared } from '@screens/Authenticated/Property/sharedStyles';
import { Styles as FormStyles } from '@screens/Authenticated/Property/NewRegisterScreen/style';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CircularProgress, Snackbar, Alert } from '@mui/material';

// ─── Draft types ──────────────────────────────────────────────────────────────

type EstadoDraft = {
  _key: string;
  id: number | null;
  uf: string;
  nome: string;
  active: boolean;
};

type CidadeDraft = {
  _key: string;
  id: number | null;
  nome: string;
  uf: string;
  active: boolean;
};

let _tempCounter = 0;
const newTempKey = () => `new-${++_tempCounter}`;

// ─── Shared cell styles ───────────────────────────────────────────────────────

const dragCellStyle: React.CSSProperties = {
  width: 36,
  textAlign: 'center',
  color: '#bbb',
  fontSize: 18,
  userSelect: 'none',
  paddingTop: 10,
  paddingBottom: 10
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: 14,
  padding: '2px 0',
  borderBottom: '1px solid transparent',
  transition: 'border-color 0.15s'
};

const focusLine = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) =>
  (e.currentTarget.style.borderBottomColor = '#125dae');
const blurLine = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) =>
  (e.currentTarget.style.borderBottomColor = 'transparent');

// ─── SortableEstadoRow ────────────────────────────────────────────────────────

interface SortableEstadoRowProps {
  row: EstadoDraft;
  onChange: (
    key: string,
    field: 'uf' | 'nome' | 'active',
    value: string | boolean
  ) => void;
}

const SortableEstadoRow: React.FC<SortableEstadoRowProps> = ({
  row,
  onChange
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: row._key });

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        background: isDragging ? '#f0f7ff' : '#ffffff',
        borderBottom: '1px solid #ececec'
      }}>
      <td
        style={{
          ...dragCellStyle,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        {...attributes}
        {...listeners}>
        ⠣
      </td>

      <td style={{ padding: '8px', width: 70 }}>
        <input
          value={row.uf}
          maxLength={2}
          onChange={(e) =>
            onChange(row._key, 'uf', e.target.value.toUpperCase())
          }
          placeholder="UF"
          style={{ ...inputStyle, width: 40, textTransform: 'uppercase' }}
          onFocus={focusLine}
          onBlur={blurLine}
        />
      </td>

      <td style={{ padding: '8px' }}>
        <input
          value={row.nome}
          onChange={(e) => onChange(row._key, 'nome', e.target.value)}
          placeholder="ex.: Paraná"
          style={inputStyle}
          onFocus={focusLine}
          onBlur={blurLine}
        />
      </td>

      <td style={{ width: 80, textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={row.active}
          onChange={(e) => onChange(row._key, 'active', e.target.checked)}
          style={{
            width: 16,
            height: 16,
            cursor: 'pointer',
            accentColor: '#125dae'
          }}
        />
      </td>
    </tr>
  );
};

// ─── SortableCidadeRow ────────────────────────────────────────────────────────

interface SortableCidadeRowProps {
  row: CidadeDraft;
  estadoOptions: EstadoDraft[];
  onChange: (
    key: string,
    field: 'nome' | 'uf' | 'active',
    value: string | boolean
  ) => void;
}

const SortableCidadeRow: React.FC<SortableCidadeRowProps> = ({
  row,
  estadoOptions,
  onChange
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: row._key });

  const validUFs = estadoOptions
    .filter((e) => e.uf.trim() !== '')
    .map((e) => e.uf);

  const isOrphan = row.uf !== '' && !validUFs.includes(row.uf);

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        background: isDragging ? '#f0f7ff' : '#ffffff',
        borderBottom: '1px solid #ececec'
      }}>
      <td
        style={{
          ...dragCellStyle,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        {...attributes}
        {...listeners}>
        ⠣
      </td>

      <td style={{ padding: '8px' }}>
        <input
          value={row.nome}
          onChange={(e) => onChange(row._key, 'nome', e.target.value)}
          placeholder="ex.: Curitiba"
          style={inputStyle}
          onFocus={focusLine}
          onBlur={blurLine}
        />
      </td>

      <td style={{ padding: '8px', width: 110 }}>
        <select
          value={row.uf}
          onChange={(e) => onChange(row._key, 'uf', e.target.value)}
          style={{
            width: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 14,
            cursor: 'pointer',
            borderBottom: '1px solid transparent',
            padding: '2px 0',
            color: isOrphan ? '#d32f2f' : 'inherit'
          }}
          onFocus={focusLine}
          onBlur={blurLine}>
          {isOrphan && <option value={row.uf}>{row.uf} ⚠</option>}
          <option value="">-- UF --</option>
          {validUFs.map((uf) => (
            <option key={uf} value={uf}>
              {uf}
            </option>
          ))}
        </select>
      </td>

      <td style={{ width: 80, textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={row.active}
          onChange={(e) => onChange(row._key, 'active', e.target.checked)}
          style={{
            width: 16,
            height: 16,
            cursor: 'pointer',
            accentColor: '#125dae'
          }}
        />
      </td>
    </tr>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

const ConfigServedCitiesScreen: React.FC = () => {
  const {
    adminEstados,
    adminCidades,
    fetchAllEstadosCidades,
    syncEstadosCidades
  } = useMetadataStore();

  const [estadoRows, setEstadoRows] = useState<EstadoDraft[]>([]);
  const [cidadeRows, setCidadeRows] = useState<CidadeDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Ref keeps estadoRows current inside memoised callbacks (no stale closure)
  const estadoRowsRef = useRef<EstadoDraft[]>([]);
  useEffect(() => {
    estadoRowsRef.current = estadoRows;
  }, [estadoRows]);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchAllEstadosCidades();
      setLoading(false);
    })();
  }, [fetchAllEstadosCidades]);

  // Store → local: estados
  useEffect(() => {
    const sorted = [...adminEstados].sort(
      (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
    );
    setEstadoRows(
      sorted.map((e) => ({
        _key: String(e.id),
        id: e.id,
        uf: e.uf,
        nome: e.nome,
        active: e.active
      }))
    );
  }, [adminEstados]);

  // Store → local: cidades
  useEffect(() => {
    const sorted = [...adminCidades].sort(
      (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
    );
    setCidadeRows(
      sorted.map((c) => ({
        _key: String(c.id),
        id: c.id,
        nome: c.nome,
        uf: c.estado?.uf ?? '',
        active: c.active
      }))
    );
  }, [adminCidades]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ── Drag ────────────────────────────────────────────────────────────────────

  const handleEstadoDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEstadoRows((prev) => {
      const oldIdx = prev.findIndex((r) => r._key === String(active.id));
      const newIdx = prev.findIndex((r) => r._key === String(over.id));
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const handleCidadeDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCidadeRows((prev) => {
      const oldIdx = prev.findIndex((r) => r._key === String(active.id));
      const newIdx = prev.findIndex((r) => r._key === String(over.id));
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  // ── Change handlers ─────────────────────────────────────────────────────────

  /**
   * When a UF is renamed, cascade the new value to every cidade that was
   * pointing at the old UF so the dropdown stays consistent.
   */
  const handleEstadoChange = useCallback(
    (key: string, field: 'uf' | 'nome' | 'active', value: string | boolean) => {
      if (field === 'uf') {
        const current = estadoRowsRef.current.find((r) => r._key === key);
        if (current && current.uf !== value) {
          const oldUF = current.uf;
          const newUF = value as string;
          setCidadeRows((prev) =>
            prev.map((c) => (c.uf === oldUF ? { ...c, uf: newUF } : c))
          );
        }
      }
      setEstadoRows((prev) =>
        prev.map((r) => (r._key === key ? { ...r, [field]: value } : r))
      );
    },
    []
  );

  const handleCidadeChange = useCallback(
    (key: string, field: 'nome' | 'uf' | 'active', value: string | boolean) => {
      setCidadeRows((prev) =>
        prev.map((r) => (r._key === key ? { ...r, [field]: value } : r))
      );
    },
    []
  );

  // ── Add handlers ────────────────────────────────────────────────────────────

  const handleAddEstado = () => {
    setEstadoRows((prev) => [
      ...prev,
      { _key: newTempKey(), id: null, uf: '', nome: '', active: true }
    ]);
  };

  const handleAddCidade = () => {
    const firstUF = estadoRows.find((e) => e.uf.trim() !== '')?.uf ?? '';
    setCidadeRows((prev) => [
      ...prev,
      { _key: newTempKey(), id: null, nome: '', uf: firstUF, active: true }
    ]);
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    try {
      const estados = estadoRows
        .filter((r) => r.uf.trim() !== '' && r.nome.trim() !== '')
        .map((r) => ({
          id: r.id,
          uf: r.uf.trim(),
          nome: r.nome.trim(),
          active: r.active
        }));

      const cidades = cidadeRows
        .filter((r) => r.nome.trim() !== '' && r.uf.trim() !== '')
        .map((r) => ({
          id: r.id,
          nome: r.nome.trim(),
          uf: r.uf.trim(),
          active: r.active
        }));

      const ok = await syncEstadosCidades(estados, cidades);
      if (!ok) throw new Error('sync failed');
      await fetchAllEstadosCidades();
      setSuccessMsg('Cidades e estados salvos com sucesso!');
    } catch {
      setErrorMsg('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // ── Shared table styles ─────────────────────────────────────────────────────

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
    background: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
  };

  const theadRowStyle: React.CSSProperties = {
    background: '#f5f5f5',
    borderBottom: '2px solid #D9D9D9',
    textAlign: 'left',
    color: '#5D5F64',
    fontWeight: 600
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 600,
    color: '#333',
    margin: '24px 0 12px'
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <AuthenticatedLayout>
      <Shared.PageSection>
        <h1>Cidades e UF</h1>
        <Shared.RectangleBar />

        {loading ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {/* ── ESTADOS ─────────────────────────────────────────────────── */}
            <h2 style={sectionTitleStyle}>Estados</h2>

            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={theadRowStyle}>
                    <th style={{ width: 36 }} />
                    <th style={{ padding: '12px 8px', width: 70 }}>UF</th>
                    <th style={{ padding: '12px 8px' }}>Nome</th>
                    <th
                      style={{
                        width: 80,
                        padding: '12px 8px',
                        textAlign: 'center'
                      }}>
                      Ativo
                    </th>
                  </tr>
                </thead>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleEstadoDragEnd}>
                  <SortableContext
                    items={estadoRows.map((r) => r._key)}
                    strategy={verticalListSortingStrategy}>
                    <tbody>
                      {estadoRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: '24px',
                              textAlign: 'center',
                              color: '#aaa'
                            }}>
                            Nenhum estado cadastrado. Clique em Adicionar.
                          </td>
                        </tr>
                      )}
                      {estadoRows.map((row) => (
                        <SortableEstadoRow
                          key={row._key}
                          row={row}
                          onChange={handleEstadoChange}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </DndContext>
              </table>
            </div>

            <div style={{ display: 'flex', marginTop: 12 }}>
              <FormStyles.Button type="button" onClick={handleAddEstado}>
                Adicionar Estado
              </FormStyles.Button>
            </div>

            {/* ── CIDADES ─────────────────────────────────────────────────── */}
            <h2 style={{ ...sectionTitleStyle, marginTop: 32 }}>Cidades</h2>

            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={theadRowStyle}>
                    <th style={{ width: 36 }} />
                    <th style={{ padding: '12px 8px' }}>Nome</th>
                    <th style={{ padding: '12px 8px', width: 110 }}>UF</th>
                    <th
                      style={{
                        width: 80,
                        padding: '12px 8px',
                        textAlign: 'center'
                      }}>
                      Ativo
                    </th>
                  </tr>
                </thead>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCidadeDragEnd}>
                  <SortableContext
                    items={cidadeRows.map((r) => r._key)}
                    strategy={verticalListSortingStrategy}>
                    <tbody>
                      {cidadeRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: '24px',
                              textAlign: 'center',
                              color: '#aaa'
                            }}>
                            Nenhuma cidade cadastrada. Clique em Adicionar.
                          </td>
                        </tr>
                      )}
                      {cidadeRows.map((row) => (
                        <SortableCidadeRow
                          key={row._key}
                          row={row}
                          estadoOptions={estadoRows}
                          onChange={handleCidadeChange}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </DndContext>
              </table>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 12,
                justifyContent: 'flex-end'
              }}>
              <FormStyles.Button type="button" onClick={handleAddCidade}>
                Adicionar Cidade
              </FormStyles.Button>
              <FormStyles.Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{ opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Salvando…' : 'SALVAR'}
              </FormStyles.Button>
            </div>
          </>
        )}
      </Shared.PageSection>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={5000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </AuthenticatedLayout>
  );
};

export default ConfigServedCitiesScreen;
